import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { formatDistanceStrict } from "date-fns";
import { DateTime } from "luxon";
import { useMemo } from "react";
import Modal from "react-modal";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import { Case, Specimen, UserContact } from "pwapi";

import { openDocument } from "shared/actions/caseDetailActions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { sortSpecimens } from "shared/utils/specimenUtils";
import { specimen_source } from "shared/utils/specimenUtils";

import { BorderlessButton } from "components/customInputs/buttons/Button";

import PatientSexAge from "../../PatientSexAge";
import Insurers from "../Insurers";

type PatientModalProps = {
  isOpen: boolean;
  caseDetailObject: CaseDetailState;
  setPatientModal: Function;
};

const Clinician = ({
  ordering_doctor,
  patient,
}: {
  ordering_doctor?: UserContact;
  patient?: CaseDetailState["patient"];
}) => {
  return (
    <>
      {ordering_doctor?.first_name}&nbsp;
      {ordering_doctor?.last_name}
      {patient?.clinic?.name && (
        <span>
          ,&nbsp;
          {patient?.clinic?.name}
        </span>
      )}
    </>
  );
};

const PatientModal = ({
  isOpen,
  caseDetailObject,
  setPatientModal,
}: PatientModalProps) => {
  const dispatch = useDispatch();

  const caseMatches = !!caseDetailObject.patient_matches?.length
    ? [...caseDetailObject.patient_matches, caseDetailObject].sort((a, b) => {
        const aDate = a.collected_date || a.received_date || a.created_at;
        const bDate = b.collected_date || b.received_date || b.created_at;
        const now = Date.now();
        return (
          (bDate ? Date.parse(bDate) : now) - (aDate ? Date.parse(aDate) : now)
        );
      })
    : [];

  const copathMatches = useMemo(() => {
    return (caseDetailObject.copath_patient_matches || []).map((match) => {
      const dom = new DOMParser().parseFromString(match, "text/html");
      const link = dom.querySelector("a[target='_blank']");
      const marks = dom.querySelectorAll("mark");
      const ps = dom.querySelectorAll("p");

      const firstName = [...marks]
        .filter(
          (s) =>
            s.textContent?.toUpperCase() ===
            caseDetailObject?.patient.first_name?.toUpperCase()
        )
        .at(0)?.textContent;
      const lastName = [...marks]
        .filter(
          (s) =>
            s.textContent?.toUpperCase() ===
            caseDetailObject?.patient.last_name?.toUpperCase()
        )
        .at(0)?.textContent;
      const dob = [...ps]
        .filter((s) => s.textContent?.includes("DOB:"))
        .at(0)
        ?.querySelector("mark")?.textContent;
      const sex = [...marks]
        .filter(
          (s) =>
            s.textContent?.toUpperCase() ===
            caseDetailObject?.patient.sex?.toUpperCase()
        )
        .at(0)?.textContent;
      const collectedDateEl = [...ps]
        .filter((s) => s.textContent?.trim().startsWith("Collection Date"))
        ?.at(0);
      const collectedDate = collectedDateEl?.textContent
        ?.replace("Collection Date:", "")
        .trim();
      const info =
        collectedDateEl?.nextElementSibling &&
        collectedDateEl?.nextElementSibling.matches("pre")
          ? collectedDateEl.nextElementSibling?.innerHTML
          : collectedDateEl?.querySelector(":scope > pre")?.innerHTML;
      return { firstName, lastName, dob, sex, collectedDate, link, info };
    });
  }, [
    caseDetailObject.copath_patient_matches,
    caseDetailObject?.patient.first_name,
    caseDetailObject?.patient.last_name,
    caseDetailObject?.patient.sex,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => setPatientModal(false)}
      contentLabel="Patient Information"
      className="Modal"
      overlayClassName="Overlay"
    >
      <h4>
        {caseDetailObject.patient.last_name},{" "}
        {caseDetailObject.patient.first_name}{" "}
        <span style={{ opacity: 0.7 }}>
          (
          <PatientSexAge
            patient={caseDetailObject.patient}
            shouldDisplayDOB={false}
          />
          )
        </span>
      </h4>
      <dl>
        {caseDetailObject.patient?.date_of_birth && (
          <>
            <dt>DOB</dt>
            <dd>
              {DateTime.fromISO(
                caseDetailObject.patient.date_of_birth
              ).toLocaleString()}
            </dd>
          </>
        )}
        <dt>Clinician</dt>
        <dd>
          <Clinician
            ordering_doctor={caseDetailObject.ordering_doctor}
            patient={caseDetailObject.patient}
          />
        </dd>
        {(caseDetailObject.self_pay ||
          (caseDetailObject.billing_type as Case.billing_type | "cash") ===
            "cash") && (
          <>
            <dt>Billing</dt>
            <dd>Self Pay</dd>
          </>
        )}
        <Insurers patient={caseDetailObject.patient} />
      </dl>

      {!!caseMatches?.length && (
        <div className="PatientMatches">
          <hr />

          <p>
            <strong>Potential Matches</strong>
          </p>
          <table>
            <tbody>
              {caseMatches.map((patientCase) => {
                const patientCaseDate =
                  patientCase.collected_date || patientCase.received_date;
                return (
                  <tr
                    key={patientCase.id}
                    className={classNames({
                      PatientMatches__on:
                        patientCase.id === caseDetailObject.id,
                    })}
                  >
                    <td>
                      {patientCase.id !== caseDetailObject.id ? (
                        <Link to={`/cases/${patientCase.id}`} target="_blank">
                          <strong>{patientCase.accession}</strong>
                        </Link>
                      ) : (
                        <strong>{patientCase.accession}</strong>
                      )}
                      <br />
                      {patientCase.type === Case.type.HISTORICAL ? (
                        <small>
                          <code>HISTORICAL</code>
                        </small>
                      ) : (
                        <small>{patientCase.status}</small>
                      )}
                    </td>
                    <td>
                      {patientCase.ordering_doctor?.first_name}&nbsp;
                      {patientCase.ordering_doctor?.last_name}
                      <br />
                      <small>{patientCase.patient?.clinic?.name}</small>
                    </td>
                    <td className="PatientMatches__specimens">
                      {sortSpecimens(patientCase.specimens as Specimen[]).map(
                        (s: Specimen) => (
                          <div key={s.id}>
                            <code>
                              <strong>{s.name}</strong>
                            </code>
                            &mdash;
                            {/* TODO: the enum of Specimen.procedure is incompatible with the union type on Case.specimens */}
                            {specimen_source(s)}
                          </div>
                        )
                      )}
                    </td>
                    <td>
                      {patientCase.id === caseDetailObject.id ? (
                        "currently viewing"
                      ) : (
                        <>
                          {patientCase.id !== caseDetailObject.id &&
                            patientCaseDate &&
                            caseDetailObject.collected_date && (
                              <>
                                {formatDistanceStrict(
                                  Date.parse(patientCaseDate),
                                  Date.parse(caseDetailObject.collected_date)
                                )}{" "}
                                {patientCaseDate <=
                                caseDetailObject.collected_date
                                  ? "before"
                                  : "later"}
                              </>
                            )}
                        </>
                      )}

                      {patientCase.finalized_report_id && (
                        <div>
                          <BorderlessButton
                            title="View Report"
                            size="small"
                            onClick={() => {
                              patientCase.id &&
                                patientCase.finalized_report_id &&
                                dispatch(
                                  openDocument(
                                    patientCase.id,
                                    patientCase.finalized_report_id
                                  )
                                );
                            }}
                          >
                            <>
                              Report &nbsp;
                              <FontAwesomeIcon
                                icon="file-export"
                                style={{ color: "var(--blue)" }}
                              />
                            </>
                          </BorderlessButton>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!!copathMatches?.length && (
        <div className="PatientMatches PatientMatches--copath">
          <hr />

          <p>
            <strong>Potential Matches From CoPath</strong>
          </p>
          <table>
            <tbody>
              {copathMatches.map((match, i) => {
                const {
                  firstName,
                  lastName,
                  dob,
                  sex,
                  collectedDate,
                  link,
                  info,
                } = match;
                return (
                  <tr key={link?.textContent || `${collectedDate}-${i}`}>
                    <td>
                      <strong>{link?.textContent}</strong>
                      {collectedDate && (
                        <>
                          <br />
                          <small>{collectedDate}</small>
                        </>
                      )}
                    </td>
                    <td>
                      {lastName},&nbsp;{firstName}&nbsp;({sex})
                      <br />
                      <small>DOB:&nbsp;{dob}</small>
                    </td>
                    <td className="PatientMatches__info">
                      {info && (
                        <small dangerouslySetInnerHTML={{ __html: info }} />
                      )}
                    </td>
                    {link && (
                      <td>
                        <a
                          href={link?.getAttribute("href") || "#"}
                          target="_blank"
                          rel="noreferrer"
                        >
                          External Link to Report&nbsp;↗
                        </a>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

export default PatientModal;
